import { type Message, strings } from "@quickpoint.me/shared";
import Viewerati from "@quickpoint.me/viewerati";
import { toPng } from "html-to-image";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useRecoilCallback, useRecoilValue } from "recoil";
import { postMessageToViewer } from "../../lib/messageBus";
import { HostId } from "../../state/atoms";
import { PageCount } from "../../state/codeSelector";
import { FiltersAreLoaded, PreviewerFontCSS } from "../../state/filters";
import { PreviewImageFamily } from "../../state/preview";
import { WaitForInitialAsyncCalls } from "../../state/selectors";
import { StackCfKeyAtom } from "../../state/stackAtoms";
import { ComposedStack } from "../../state/stackSelectors";
import { previewerFilter } from "./RenderPreviewGenerator";

function PreviewGenerator({
	landscape,
	cssId,
}: {
	landscape: boolean;
	cssId: string;
	$showLandscapePreview: boolean;
	$showPortraitPreview: boolean;
}) {
	const hostId = useRecoilValue(HostId);
	const pageCount = useRecoilValue(PageCount);
	const ref = useRef<HTMLDivElement>(null);
	const [snapQueue, setSnapQueue] = useState<
		{ page: number; pending: boolean }[]
	>([]);
	const [initialized, setInitialized] = useState(false);
	const [previewerIsReady, setPreviewerIsReady] = useState(false);
	const filtersAreLoaded = useRecoilValue(FiltersAreLoaded);
	const previewerFontCSS = useRecoilValue(PreviewerFontCSS);
	const stackCfKey = useRecoilValue(StackCfKeyAtom);

	const authorIsReady = useRecoilValue(WaitForInitialAsyncCalls);
	const stack = useRecoilValue(ComposedStack);

	// Use a ref to hold hostId to prevent stale closures in callbacks
	const hostIdRef = useRef(hostId);
	useEffect(() => {
		hostIdRef.current = hostId;
	}, [hostId]);

	const setPreviewImage = useRecoilCallback(
		({ set }) =>
			(idx: number, dataUrl: string) => {
				set(PreviewImageFamily({ stackCfKey, pageId: cssId + idx }), dataUrl);
			},
		[cssId, stackCfKey],
	);

	useEffect(() => {
		setInitialized(false);
	}, []);

	// When pageCount grows, append only the new pages to the queue.
	useEffect(() => {
		setSnapQueue((prev) => {
			if (pageCount <= prev.length) return prev;
			const newPages = [];
			for (let p = prev.length + 1; p <= pageCount; p++) {
				newPages.push({ page: p, pending: true });
			}
			return [...prev, ...newPages];
		});
	}, [pageCount]);

	useEffect(() => {
		if (!previewerIsReady) return;
		setInitialized(false);
	}, [previewerIsReady]);

	// send the current stack to the previewer
	useEffect(() => {
		if (
			previewerIsReady &&
			authorIsReady &&
			stack.ownerName !== strings.common.unauthorized &&
			stack.stackCfKey !== strings.unset
		) {
			const message: Message = {
				hostId,
				type: "preview",
				action: strings.messages.edit,
				payload: stack,
			};

			setTimeout(() => {
				postMessageToViewer(message);
			}, 0);
		}
	}, [stack, authorIsReady, hostId, previewerIsReady]);

	// create the snapQueue
	useLayoutEffect(() => {
		if (initialized) return;
		let initializingPage = 1;
		const queue = [];
		while (initializingPage <= pageCount) {
			queue.push({ page: initializingPage, pending: true });
			initializingPage++;
		}
		setSnapQueue(queue);
		setInitialized(true);
	}, [pageCount, initialized]);

	// KICK-OFF: Start the process when the previewer is ready and the queue is built.
	useEffect(() => {
		if (!previewerIsReady || !filtersAreLoaded || snapQueue.length === 0)
			return;

		const firstPendingItem = snapQueue.find((item) => item.pending);
		if (firstPendingItem) {
			const message: Message = {
				hostId,
				type: "preview",
				action: strings.messages.goToPage,
				payload: firstPendingItem.page,
			};

			postMessageToViewer(message);
		}
	}, [previewerIsReady, filtersAreLoaded, snapQueue, hostId]);

	// THE LOOP: Listen for snap requests and trigger the next page.
	useEffect(() => {
		const bc = new BroadcastChannel(strings.broadcastChannel.author);

		const snapPreview = async (idx: number) => {
			if (!ref.current) return;
			try {
				const dataUrl = await toPng(ref.current, {
					canvasWidth: landscape ? 168 : 100,
					canvasHeight: landscape ? 100 : 168,
					height: landscape ? 100 : 168,
					width: landscape ? 168 : 100,
					pixelRatio: 1,
					filter: previewerFilter,
					fontEmbedCSS: previewerFontCSS,
					imagePlaceholder: "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==",
				});

				setPreviewImage(idx, dataUrl);
			} catch (err) {
				// snap failed — silently continue
			}
		};

		bc.onmessage = async (e) => {
			if (!e.data) return;
			const { action, payload, type } = e.data as Message;
			if (type !== "preview") return;

			if (action === strings.messages.restartPreview) {
				setInitialized(false);
			}

			if (
				action === strings.messages.requestPreviewSnap &&
				typeof payload === "number"
			) {
				try {
					await snapPreview(payload);
				} finally {
					setSnapQueue((currentQueue) => {
						const updatedQueue = currentQueue.map((item) => {
							if (item.page === payload) {
								return { ...item, pending: false };
							}
							return item;
						});

						const nextPendingItem = updatedQueue.find((item) => item.pending);

						if (nextPendingItem) {
							const message: Message = {
								hostId: hostIdRef.current,
								type: "preview",
								action: strings.messages.goToPage,
								payload: nextPendingItem.page,
							};
							postMessageToViewer(message);
						}

						return updatedQueue;
					});
				}
			}
		};

		return () => {
			bc.close();
		};
	}, [landscape, setPreviewImage, previewerFontCSS]);

	return (
		<div
			ref={ref}
			id={cssId}
			className={`preview-generator ${strings.css.previewPane}`}
		>
			<Viewerati
				key={`preview-generator ${strings.css.previewPane}`}
				hostId={hostId}
				containerSelector={cssId}
				isInAuthor={true}
				isPreviewer={true}
				onReady={() => setPreviewerIsReady(true)}
			/>
		</div>
	);
}

export default PreviewGenerator;
